import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TextFields as TextIcon,
  Image as ImageIcon,
  Splitscreen as DividerIcon,
  Assignment as FormIcon,
  ViewModule as ComponentIcon,
  Description as FormSelectIcon
} from '@mui/icons-material';
import { PageComponent } from '../../types/pages';
import { FormDefinition } from '../../types/forms';
import { ComponentDefinition } from '../../types/components';
import { FormService } from '../../services/formService';
import { ComponentService } from '../../services/componentService';
import { Logger } from '../../utils/logger';
import { v4 as uuidv4 } from 'uuid';

interface ComponentPaletteProps {
  onAddComponent: (component: PageComponent) => void;
}

const ComponentPalette: React.FC<ComponentPaletteProps> = ({ onAddComponent }) => {
  const [forms, setForms] = useState<FormDefinition[]>([]);
  const [customComponents, setCustomComponents] = useState<ComponentDefinition[]>([]);
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<string>('');
  const [textContent, setTextContent] = useState('');

  useEffect(() => {
    loadForms();
    loadCustomComponents();
  }, []);

  const loadForms = async () => {
    try {
      const formsData = await FormService.getForms();
      setForms(Array.isArray(formsData) ? formsData : []);
    } catch (error) {
      Logger.apiError('Failed to load forms for component palette', error as Error, {
        component: 'ComponentPalette',
        operation: 'loadForms'
      });
      setForms([]); // Ensure forms is always an array, even on error
    }
  };

  const loadCustomComponents = async () => {
    try {
      const componentsData = await ComponentService.getComponents({ isActive: true });
      setCustomComponents(Array.isArray(componentsData) ? componentsData : []);
    } catch (error) {
      Logger.apiError('Failed to load custom components for component palette', error as Error, {
        component: 'ComponentPalette',
        operation: 'loadCustomComponents'
      });
      setCustomComponents([]); // Ensure components is always an array, even on error
    }
  };

  const addTextComponent = () => {
    const component: PageComponent = {
      id: uuidv4(),
      type: 'text',
      order: 0,
      props: {
        content: textContent || 'New text component',
        variant: 'body1'
      }
    };
    onAddComponent(component);
    setTextContent('');
    setTextDialogOpen(false);
  };

  const addImageComponent = () => {
    const component: PageComponent = {
      id: uuidv4(),
      type: 'image',
      order: 0,
      props: {
        src: '',
        alt: 'Image',
        width: '100%',
        height: 'auto'
      }
    };
    onAddComponent(component);
  };

  const addDividerComponent = () => {
    const component: PageComponent = {
      id: uuidv4(),
      type: 'divider',
      order: 0,
      props: {
        variant: 'fullWidth',
        style: 'solid'
      }
    };
    onAddComponent(component);
  };

  const addCustomComponent = (customComponent: ComponentDefinition) => {
    const component: PageComponent = {
      id: uuidv4(),
      type: 'component',
      componentId: customComponent.id,
      order: 0,
      props: customComponent.props.reduce((acc, prop) => {
        acc[prop.name] = prop.defaultValue;
        return acc;
      }, {} as Record<string, unknown>)
    };
    onAddComponent(component);
  };
    
  const addFormComponent = () => {
    if (!selectedForm) return;
    
    const form = forms.find(f => f.id === selectedForm);
    const component: PageComponent = {
      id: uuidv4(),
      type: 'form',
      order: 0,
      props: {
        formId: selectedForm,
        title: form?.name || 'Form',
        showTitle: true
      }
    };
    onAddComponent(component);
    setSelectedForm('');
    setFormDialogOpen(false);
  };

  const basicComponents = [
    {
      type: 'text',
      label: 'Text',
      description: 'Add text content',
      icon: <TextIcon />,
      action: () => setTextDialogOpen(true)
    },
    {
      type: 'image',
      label: 'Image',
      description: 'Add an image',
      icon: <ImageIcon />,
      action: addImageComponent
    },
    {
      type: 'divider',
      label: 'Divider',
      description: 'Add a horizontal divider',
      icon: <DividerIcon />,
      action: addDividerComponent
    }
  ];

  return (
    <Paper sx={{ p: 2, height: 'fit-content' }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ComponentIcon />
        Component Palette
      </Typography>

      {/* Basic Components */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Basic Components</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List dense>
            {basicComponents.map((component) => (
              <ListItem
                key={component.type}
                component="li"
                sx={{ 
                  borderRadius: 1, 
                  mb: 0.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={component.action}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {component.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={component.label}
                  secondary={component.description}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Custom Components */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ComponentIcon />
            <Typography variant="subtitle1">Custom Components</Typography>
            <Chip label={customComponents.length} size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {customComponents.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                No custom components available. Create components first.
              </Typography>
            </Box>
          ) : (
            <List dense>
              {customComponents.map((component) => (
                <ListItem
                  key={component.id}
                  component="li"
                  sx={{ 
                    borderRadius: 1, 
                    mb: 0.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => addCustomComponent(component)}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ComponentIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={component.displayName}
                    secondary={component.description || `${component.props.length} properties`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Forms */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormIcon />
            <Typography variant="subtitle1">Forms</Typography>
            <Chip label={forms.length} size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FormSelectIcon />}
              onClick={() => setFormDialogOpen(true)}
              disabled={forms.length === 0}
            >
              Add Form
            </Button>
            {forms.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                No forms available. Create a form first.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Text Component Dialog */}
      <Dialog open={textDialogOpen} onClose={() => setTextDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Text Component</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Text Content"
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ mt: 1 }}
            placeholder="Enter your text content..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextDialogOpen(false)}>Cancel</Button>
          <Button onClick={addTextComponent} variant="contained" disabled={!textContent.trim()}>
            Add Text
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form Selection Dialog */}
      <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Form</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Choose a form</InputLabel>
            <Select
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value)}
              label="Choose a form"
            >
              {(forms || []).map((form) => (
                <MenuItem key={form.id} value={form.id}>
                  <Box>
                    <Typography variant="body1">{form.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {form.description || 'No description'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDialogOpen(false)}>Cancel</Button>
          <Button onClick={addFormComponent} variant="contained" disabled={!selectedForm}>
            Add Form
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ComponentPalette;